import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiChevronUp } from 'react-icons/fi';
import { useTabs } from '../context/TabsContext';
import { useSidebar } from '../context/SidebarContext';
import { getTabStyle } from './tabColors';

const GROUP_MIN = 3;

const kindLabel = (kind) => {
    if (!kind) return 'Pages';
    const base = kind.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return base.endsWith('s') ? base : `${base}s`;
};

const TabBar = () => {
    const navigate = useNavigate();
    const { tabs, activeTabId, closeTab, closeAllTabs } = useTabs();
    const { isMobile } = useSidebar();
    const [openGroup, setOpenGroup] = useState(null);
    const barRef = useRef(null);

    useEffect(() => {
        if (!openGroup) return;
        const onDown = (e) => {
            if (barRef.current && barRef.current.contains(e.target)) return;
            setOpenGroup(null);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [openGroup]);

    if (tabs.length === 0) return null;

    const handleTabClick = (tab) => {
        setOpenGroup(null);
        navigate(tab.url);
    };

    const handleMouseDown = (e, tabId) => {
        if (e.button === 1) {
            e.preventDefault();
            closeTab(tabId);
        }
    };

    const handleCloseClick = (e, tabId) => {
        e.stopPropagation();
        closeTab(tabId);
    };

    // Kinds with GROUP_MIN+ tabs collapse into one group chip with a fly-up list.
    const countByKind = {};
    tabs.forEach(t => {
        const k = t.kind || '_other';
        countByKind[k] = (countByKind[k] || 0) + 1;
    });
    const groupedKinds = new Set(
        Object.keys(countByKind).filter(k => k !== '_other' && countByKind[k] >= GROUP_MIN)
    );

    const items = [];
    const renderedGroups = new Set();
    tabs.forEach(tab => {
        const k = tab.kind || '_other';
        if (groupedKinds.has(k)) {
            if (!renderedGroups.has(k)) {
                renderedGroups.add(k);
                items.push({ type: 'group', kind: k, tabs: tabs.filter(t => (t.kind || '_other') === k) });
            }
        } else {
            items.push({ type: 'tab', tab });
        }
    });

    return (
        <div
            ref={barRef}
            className="bg-gray-100 border-t border-gray-300 print:hidden"
            style={{
                position: 'fixed',
                bottom: isMobile ? 60 : 0,
                left: 0,
                right: 0,
                zIndex: 60,
            }}
        >
            <div className="flex items-stretch">
                <div className="flex items-stretch flex-1 min-w-0 overflow-x-auto no-scrollbar">
                    {items.map((item, idx) => {
                        if (item.type === 'tab') {
                            const tab = item.tab;
                            const isActive = tab.id === activeTabId;
                            const style = getTabStyle(tab.kind);
                            return (
                                <div
                                    key={tab.id}
                                    onClick={() => handleTabClick(tab)}
                                    onMouseDown={(e) => handleMouseDown(e, tab.id)}
                                    className={`group flex items-center gap-2 px-3 py-2 border-r cursor-pointer select-none flex-shrink-0 max-w-[240px] ${
                                        isActive ? style.active : style.inactive
                                    }`}
                                    title={tab.title}
                                >
                                    <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${style.dot}`} />
                                    <span className="truncate text-sm">{tab.title}</span>
                                    <button
                                        type="button"
                                        onClick={(e) => handleCloseClick(e, tab.id)}
                                        className="flex-shrink-0 opacity-50 hover:opacity-100 hover:text-red-600"
                                        aria-label={`Close ${tab.title}`}
                                    >
                                        <FiX size={14} />
                                    </button>
                                </div>
                            );
                        }

                        const { kind, tabs: groupTabs } = item;
                        const style = getTabStyle(kind);
                        const containsActive = groupTabs.some(t => t.id === activeTabId);
                        const isOpen = openGroup?.kind === kind;
                        return (
                            <div key={`group-${kind}`} className="relative flex-shrink-0">
                                <div
                                    onClick={(e) => {
                                        if (isOpen) { setOpenGroup(null); return; }
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setOpenGroup({ kind, left: rect.left, top: rect.top });
                                    }}
                                    className={`flex items-center gap-2 px-3 py-2 border-r cursor-pointer select-none h-full ${
                                        containsActive ? style.active : style.inactive
                                    }`}
                                    title={`${kindLabel(kind)} (${groupTabs.length})`}
                                >
                                    <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${style.dot}`} />
                                    <span className="text-sm font-medium">{kindLabel(kind)}</span>
                                    <span className="text-xs opacity-70">({groupTabs.length})</span>
                                    <FiChevronUp size={13} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                </div>
                                {isOpen && (
                                    <div
                                        className="min-w-[240px] max-w-[320px] max-h-[50vh] overflow-y-auto bg-white border border-gray-300 rounded-t-md shadow-lg"
                                        style={{
                                            position: 'fixed',
                                            left: Math.min(openGroup.left, window.innerWidth - 330),
                                            bottom: window.innerHeight - openGroup.top + 2,
                                            zIndex: 70,
                                        }}
                                    >
                                        {groupTabs.map(tab => {
                                            const isActive = tab.id === activeTabId;
                                            return (
                                                <div
                                                    key={tab.id}
                                                    onClick={() => handleTabClick(tab)}
                                                    onMouseDown={(e) => handleMouseDown(e, tab.id)}
                                                    className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer text-sm border-b last:border-b-0 border-gray-100 ${
                                                        isActive ? 'bg-blue-50 font-medium' : 'hover:bg-gray-50'
                                                    }`}
                                                    title={tab.title}
                                                >
                                                    <span className={`flex-shrink-0 w-1.5 h-1.5 rounded-full ${style.dot}`} />
                                                    <span className="truncate flex-1">{tab.title}</span>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleCloseClick(e, tab.id)}
                                                        className="flex-shrink-0 opacity-50 hover:opacity-100 hover:text-red-600"
                                                        aria-label={`Close ${tab.title}`}
                                                    >
                                                        <FiX size={14} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {tabs.length >= 2 && (
                    <button
                        type="button"
                        onClick={closeAllTabs}
                        className="flex-shrink-0 px-3 py-2 text-xs text-gray-600 bg-gray-100 hover:text-red-600 hover:bg-gray-200 border-l border-gray-300"
                    >
                        Close all
                    </button>
                )}
            </div>
        </div>
    );
};

export default TabBar;
